import { MouseEvent, PointerEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { clamp } from '@util/number';
import { useDerivedState } from '@hooks/useDerivedState';

type RowResizeProps = {
    initialHeight: number;
    onHeightChange: (height: number) => void;
    onHeightReset?: () => void;
    position?: 'top' | 'bottom';
    minHeight?: number;
    maxHeight?: number;
};

export const useRowResize = ({
    initialHeight,
    onHeightChange,
    onHeightReset,
    position = 'top',
    minHeight = 0,
    maxHeight = 1e5,
}: RowResizeProps) => {
    const resolvedHeight = initialHeight ?? minHeight;
    const [height, setHeight] = useDerivedState(resolvedHeight, [resolvedHeight]);
    const [dragging, setDragging] = useState(false);

    // Use refs to avoid rebinding mouse handlers on every move
    const anchorRef = useRef<number>(0);
    const draggingRef = useRef<boolean>(false);
    const heightRef = useRef<number>(height);

    draggingRef.current = dragging;
    heightRef.current = height;

    const resizeHandlers = useMemo(() => {
        const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
            const { current: height } = heightRef;

            e.preventDefault();
            e.stopPropagation();

            // Track click anchor relative to 0
            const sign = position == 'bottom' ? -1 : 1;
            anchorRef.current = sign * e.clientY - height;
            setDragging((draggingRef.current = true));

            (e.target as Element)?.setPointerCapture(e.pointerId);
        };

        const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
            const { current: anchor } = anchorRef;
            const { current: dragging } = draggingRef;

            if (!dragging) return;
            e.stopPropagation();

            const sign = position == 'bottom' ? -1 : 1;
            const height = clamp(Math.round(sign * e.clientY - anchor), minHeight, maxHeight);
            setHeight((heightRef.current = height));
        };

        const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
            const { current: height } = heightRef;

            e.stopPropagation();
            if (height != resolvedHeight) onHeightChange(height);
            setDragging((draggingRef.current = false));

            (e.target as Element)?.releasePointerCapture(e.pointerId);
        };

        const onDoubleClick = (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            onHeightReset?.();
        };

        return { onPointerDown, onPointerMove, onPointerUp, onDoubleClick };
    }, [resolvedHeight, setHeight, onHeightChange, onHeightReset, minHeight, maxHeight, position]);

    useLayoutEffect(() => {
        if (!dragging) return;

        // Add workaround for chrome bug - macOS pointerUp while moving mouse does not fire when pointer capture is active
        // https://bugs.chromium.org/p/chromium/issues/detail?id=1459632&q=pointerup%20event&can=2
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        document.addEventListener('pointerup', resizeHandlers.onPointerUp as any);
        return () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            document.removeEventListener('pointerup', resizeHandlers.onPointerUp as any);
        };
    }, [dragging, resizeHandlers]);

    return { height, dragging, resizeHandlers };
};
